import { A, useNavigate, useSearchParams } from '@solidjs/router';
import {
  type Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
} from 'solid-js';
import {
  cancelExecution,
  fetchAgents,
  fetchExecution,
  fetchExecutions,
  fetchProjects,
} from '../services/api';

type ViewMode = 'list' | 'detail';
type PageSize = 30 | 50 | 100;

const Executions: Component = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pagination state
  const [pageSize, setPageSize] = createSignal<PageSize>(30);
  const [currentPage, setCurrentPage] = createSignal(1);

  // Filter state
  const [selectedStatus, setSelectedStatus] = createSignal<string>(searchParams.status || '');
  const [selectedProjectId, setSelectedProjectId] = createSignal<string>(
    searchParams.projectId || '',
  );
  const [selectedAgentId, setSelectedAgentId] = createSignal<string>(searchParams.agentId || '');

  // UI state
  const [viewMode, setViewMode] = createSignal<ViewMode>('list');
  const [selectedExecutionId, setSelectedExecutionId] = createSignal<string | null>(
    searchParams.executionId || null,
  );
  const [showCancelDialog, setShowCancelDialog] = createSignal(false);
  const [isCancelling, setIsCancelling] = createSignal(false);
  // Auto-refresh disabled by default - users can click to view real-time updates on detail page
  const [autoRefresh, setAutoRefresh] = createSignal(false);

  // Computed pagination params
  const paginationParams = createMemo(() => ({
    limit: pageSize(),
    offset: (currentPage() - 1) * pageSize(),
    status: selectedStatus() || undefined,
    projectId: selectedProjectId() || undefined,
    agentId: selectedAgentId() || undefined,
  }));

  // Fetch executions with pagination
  const [executionsData, { refetch }] = createResource(paginationParams, fetchExecutions);

  // Fetch projects for filter dropdown
  const [projectsData] = createResource(async () => {
    return fetchProjects({ limit: 100 });
  });

  // Fetch agents for filter dropdown
  const [agentsData] = createResource(async () => {
    return fetchAgents({ limit: 100 });
  });

  // Fetch selected execution details
  const [selectedExecution, { refetch: refetchSelectedExecution }] = createResource(
    () => selectedExecutionId(),
    async (id) => {
      if (!id) return null;
      return fetchExecution(id);
    },
  );

  // Auto-refresh for running executions
  createEffect(() => {
    if (!autoRefresh()) return;

    const interval = setInterval(() => {
      refetch();
      if (selectedExecutionId() && selectedExecution()?.status === 'running') {
        refetchSelectedExecution();
      }
    }, 2000); // Refresh every 2 seconds for faster updates

    onCleanup(() => clearInterval(interval));
  });

  // Derived values
  const executions = createMemo(() => executionsData()?.items || []);
  const totalCount = createMemo(() => executionsData()?.total || 0);
  const totalPages = createMemo(() => Math.ceil(totalCount() / pageSize()));
  const projects = createMemo(() => projectsData()?.items || []);
  const agents = createMemo(() => agentsData()?.items || []);

  // Reset to page 1 when filters change
  createEffect(() => {
    selectedStatus();
    selectedProjectId();
    selectedAgentId();
    pageSize();
    setCurrentPage(1);
  });

  // Count running executions
  const runningCount = createMemo(() => {
    return executions().filter((e) => e.status === 'running' || e.status === 'pending').length;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatDuration = (startedAt?: string, completedAt?: string) => {
    if (!startedAt) return '-';
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diff = end.getTime() - start.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getStatusConfig = (status: string) => {
    const config: Record<string, { color: string; icon: string; label: string }> = {
      running: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: '●', label: 'Running' },
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: '○', label: 'Pending' },
      completed: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: '✓', label: 'Completed' },
      failed: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: '✕', label: 'Failed' },
      cancelled: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: '—', label: 'Cancelled' },
    };
    return config[status] || config.cancelled;
  };

  const getStatusBadgeClass = (status: string) => {
    return getStatusConfig(status).color;
  };

  const clearFilters = () => {
    setSelectedStatus('');
    setSelectedProjectId('');
    setSelectedAgentId('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = createMemo(
    () => selectedStatus() !== '' || selectedProjectId() !== '' || selectedAgentId() !== '',
  );

  const handleSelectExecution = (executionId: string) => {
    // Navigate to the dedicated ExecutionDetail page for full real-time monitoring
    navigate(`/executions/${executionId}`);
  };

  const handleBackToList = () => {
    setSelectedExecutionId(null);
    setViewMode('list');
    setSearchParams({ executionId: undefined });
  };

  const handleCancelExecution = async () => {
    if (!selectedExecutionId()) return;

    setIsCancelling(true);
    try {
      await cancelExecution(selectedExecutionId()!);
      refetchSelectedExecution();
      refetch();
    } catch (error) {
      console.error('Failed to cancel execution:', error);
      alert('Failed to cancel execution. Please try again.');
    } finally {
      setIsCancelling(false);
      setShowCancelDialog(false);
    }
  };

  const getProjectName = (projectId?: string) => {
    if (!projectId) return '-';
    const project = projects().find((p) => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const getAgentName = (agentId?: string) => {
    if (!agentId) return '-';
    const agent = agents().find((a) => a.id === agentId);
    return agent?.name || 'Unknown Agent';
  };

  return (
    <div>
      {/* Cancel Confirmation Dialog */}
      <Show when={showCancelDialog()}>
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-gray-800 rounded-lg p-6 max-w-md text-center border border-gray-700">
            <div class="mb-4 flex justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f59e0b"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 class="text-xl font-medium mb-4">Cancel Execution</h2>
            <p class="text-gray-400 mb-6">
              Are you sure you want to cancel this execution? This action cannot be undone.
            </p>
            <div class="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setShowCancelDialog(false)}
                disabled={isCancelling()}
                class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors disabled:opacity-50"
              >
                Keep Running
              </button>
              <button
                type="button"
                onClick={handleCancelExecution}
                disabled={isCancelling()}
                class="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-50"
              >
                {isCancelling() ? 'Cancelling...' : 'Cancel Execution'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Detail View */}
      <Show when={viewMode() === 'detail' && selectedExecution()}>
        {(execution) => (
          <div>
            {/* Detail Header */}
            <div class="mb-6">
              <div class="flex items-center gap-4 mb-4">
                <button
                  type="button"
                  onClick={handleBackToList}
                  class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m12 19-7-7 7-7" />
                    <path d="M19 12H5" />
                  </svg>
                </button>
                <div class="flex-1">
                  <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
                    Execution Details
                    <Show when={execution().status === 'running'}>
                      <span class="flex items-center gap-2 text-blue-400 text-base">
                        <span class="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                        Live
                      </span>
                    </Show>
                  </h1>
                  <p class="text-gray-400 text-sm mt-1">
                    Started {formatRelativeTime(execution().started_at || execution().created_at)}
                  </p>
                </div>
                <Show when={execution().status === 'running' || execution().status === 'pending'}>
                  <button
                    type="button"
                    onClick={() => setShowCancelDialog(true)}
                    class="px-4 py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </Show>
              </div>

              {/* Status badges */}
              <div class="flex flex-wrap gap-2">
                <span
                  class={`px-3 py-1 rounded-full text-sm border ${getStatusBadgeClass(
                    execution().status,
                  )}`}
                >
                  {execution().status === 'running' && (
                    <span class="inline-block w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse" />
                  )}
                  {execution().status}
                </span>
                <Show when={execution().project_id}>
                  <A
                    href={`/projects/${execution().project_id}`}
                    class="px-3 py-1 rounded-full text-sm bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                  >
                    {getProjectName(execution().project_id)}
                  </A>
                </Show>
                <Show when={execution().agent_id}>
                  <A
                    href={`/agents/${execution().agent_id}/edit`}
                    class="px-3 py-1 rounded-full text-sm bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
                  >
                    {getAgentName(execution().agent_id)}
                  </A>
                </Show>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div class="lg:col-span-2 space-y-6">
                {/* Current Step / Progress */}
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="text-cyan-400"
                    >
                      <path d="M12 8v4l3 3" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                    Progress
                  </h2>

                  <Show when={execution().current_step}>
                    <div class="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div class="flex items-center gap-2 mb-2">
                        <span class="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                        <span class="text-sm text-blue-400 font-medium">Current Step</span>
                      </div>
                      <p class="text-gray-200">{execution().current_step}</p>
                    </div>
                  </Show>

                  <Show
                    when={execution().completed_steps && execution().completed_steps!.length > 0}
                  >
                    <div class="space-y-2">
                      <h3 class="text-sm font-medium text-gray-400 mb-3">Completed Steps</h3>
                      <For each={execution().completed_steps}>
                        {(step, index) => (
                          <div class="flex items-start gap-3 py-2">
                            <div class="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </div>
                            <div class="flex-1">
                              <p class="text-gray-300 text-sm">{step}</p>
                            </div>
                            <span class="text-xs text-gray-500">Step {index() + 1}</span>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>

                  <Show
                    when={
                      !execution().current_step &&
                      (!execution().completed_steps || execution().completed_steps!.length === 0)
                    }
                  >
                    <p class="text-gray-500 text-center py-4">No step information available</p>
                  </Show>
                </section>

                {/* Error Display */}
                <Show when={execution().error}>
                  <section class="bg-red-500/10 rounded-lg border border-red-500/30 p-6">
                    <h2 class="text-lg font-medium text-red-400 mb-4 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      Error
                    </h2>
                    <pre class="text-red-300 text-sm whitespace-pre-wrap bg-red-500/10 p-4 rounded-lg overflow-x-auto">
                      {execution().error}
                    </pre>
                  </section>
                </Show>

                {/* Input */}
                <Show when={execution().input}>
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-gray-400"
                      >
                        <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" />
                        <polyline points="14 2 14 8 20 8" />
                        <path d="m3 15 2 2 4-4" />
                      </svg>
                      Input
                    </h2>
                    <pre class="text-gray-300 text-sm whitespace-pre-wrap bg-gray-900/50 p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(execution().input, null, 2)}
                    </pre>
                  </section>
                </Show>

                {/* Output */}
                <Show when={execution().output}>
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-green-400"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                      Output
                    </h2>
                    <pre class="text-gray-300 text-sm whitespace-pre-wrap bg-gray-900/50 p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(execution().output, null, 2)}
                    </pre>
                  </section>
                </Show>
              </div>

              {/* Sidebar */}
              <div class="space-y-6">
                {/* Statistics */}
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 class="text-lg font-medium text-gray-200 mb-4">Details</h2>
                  <div class="space-y-4">
                    <div class="flex items-center justify-between">
                      <span class="text-gray-400">Status</span>
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                          execution().status,
                        )}`}
                      >
                        {execution().status}
                      </span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-gray-400">Duration</span>
                      <span class="text-gray-200 font-medium">
                        {formatDuration(execution().started_at, execution().completed_at)}
                      </span>
                    </div>
                    <Show when={execution().tokens_used}>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Tokens Used</span>
                        <span class="text-gray-200 font-medium">
                          {execution().tokens_used?.toLocaleString()}
                        </span>
                      </div>
                    </Show>
                    <Show when={execution().compute_cost}>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Compute Cost</span>
                        <span class="text-gray-200 font-medium">
                          ${execution().compute_cost?.toFixed(4)}
                        </span>
                      </div>
                    </Show>
                    <div class="flex items-center justify-between">
                      <span class="text-gray-400">Created</span>
                      <span class="text-gray-200 text-sm">
                        {formatDate(execution().created_at)}
                      </span>
                    </div>
                    <Show when={execution().started_at}>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Started</span>
                        <span class="text-gray-200 text-sm">
                          {formatDate(execution().started_at!)}
                        </span>
                      </div>
                    </Show>
                    <Show when={execution().completed_at}>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Completed</span>
                        <span class="text-gray-200 text-sm">
                          {formatDate(execution().completed_at!)}
                        </span>
                      </div>
                    </Show>
                  </div>
                </section>

                {/* Artifacts */}
                <Show when={execution().artifact_ids && execution().artifact_ids!.length > 0}>
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-200 mb-4">Artifacts</h2>
                    <div class="space-y-2">
                      <For each={execution().artifact_ids}>
                        {(artifactId) => (
                          <A
                            href={`/artifacts?id=${artifactId}`}
                            class="flex items-center gap-2 p-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="text-purple-400"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <span class="text-sm truncate">{artifactId}</span>
                          </A>
                        )}
                      </For>
                    </div>
                  </section>
                </Show>

                {/* Related Links */}
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 class="text-lg font-medium text-gray-200 mb-4">Links</h2>
                  <div class="space-y-2">
                    <Show when={execution().project_id}>
                      <A
                        href={`/projects/${execution().project_id}`}
                        class="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M3 3v18h18" />
                          <path d="m19 9-5 5-4-4-3 3" />
                        </svg>
                        View Project
                      </A>
                    </Show>
                    <Show when={execution().agent_id}>
                      <A
                        href={`/agents/${execution().agent_id}/edit`}
                        class="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12h.01M15 12h.01M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5"
                          />
                        </svg>
                        View Agent
                      </A>
                    </Show>
                  </div>
                </section>
              </div>
            </div>
          </div>
        )}
      </Show>

      {/* List View */}
      <Show when={viewMode() === 'list'}>
        {/* Page Header */}
        <div class="mb-6">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Executions
              </h1>
              <p class="text-gray-400 text-sm mt-1">Monitor and track execution history</p>
            </div>
            <div class="flex items-center gap-4">
              <Show when={runningCount() > 0}>
                <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400">
                  <span class="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                  <span class="text-sm font-medium">{runningCount()} running</span>
                </div>
              </Show>
              <label class="flex items-center gap-2 text-sm text-gray-400">
                <input
                  type="checkbox"
                  checked={autoRefresh()}
                  onChange={(e) => setAutoRefresh(e.currentTarget.checked)}
                  class="rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                />
                Auto-refresh
              </label>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div class="mb-6 bg-gray-800/50 rounded-lg border border-gray-700 p-4">
          <div class="flex flex-wrap items-center gap-4">
            {/* Status Filter */}
            <select
              value={selectedStatus()}
              onChange={(e) => setSelectedStatus(e.currentTarget.value)}
              class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* Project Filter */}
            <select
              value={selectedProjectId()}
              onChange={(e) => setSelectedProjectId(e.currentTarget.value)}
              class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="">All Projects</option>
              <For each={projects()}>
                {(project) => <option value={project.id}>{project.name}</option>}
              </For>
            </select>

            {/* Agent Filter */}
            <select
              value={selectedAgentId()}
              onChange={(e) => setSelectedAgentId(e.currentTarget.value)}
              class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="">All Agents</option>
              <For each={agents()}>{(agent) => <option value={agent.id}>{agent.name}</option>}</For>
            </select>

            {/* Clear Filters */}
            <Show when={hasActiveFilters()}>
              <button
                type="button"
                onClick={clearFilters}
                class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm transition-colors"
              >
                Clear Filters
              </button>
            </Show>

            {/* Refresh Button */}
            <button
              type="button"
              onClick={() => refetch()}
              class="ml-auto p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
              title="Refresh"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                <path d="M16 16h5v5" />
              </svg>
            </button>
          </div>
        </div>

        {/* Results Count and Page Size */}
        <div class="mb-4 flex items-center justify-between text-sm text-gray-400">
          <span>
            {totalCount()} execution{totalCount() !== 1 ? 's' : ''} found
          </span>
          <div class="flex items-center gap-2">
            <span>Show:</span>
            <select
              value={pageSize()}
              onChange={(e) => setPageSize(Number(e.currentTarget.value) as PageSize)}
              class="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 focus:outline-none focus:border-purple-500"
            >
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        <Show when={executionsData.loading}>
          <div class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
          </div>
        </Show>

        {/* Empty State */}
        <Show when={!executionsData.loading && executions().length === 0}>
          <div class="text-center py-12">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="mx-auto text-gray-600 mb-4"
            >
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            <h3 class="text-xl font-medium text-gray-300 mb-2">No executions found</h3>
            <p class="text-gray-500 mb-6">
              {hasActiveFilters()
                ? 'Try adjusting your filters'
                : 'Run a project or agent to see execution history'}
            </p>
            <A
              href="/projects"
              class="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-medium transition-all"
            >
              View Projects
            </A>
          </div>
        </Show>

        {/* Executions Table */}
        <Show when={!executionsData.loading && executions().length > 0}>
          <div class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <table class="w-full">
              <thead class="bg-gray-900/50 border-b border-gray-700">
                <tr>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                    Project / Agent
                  </th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">
                    Current Step
                  </th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Started</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Duration</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Tokens</th>
                  <th class="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-700">
                <For each={executions()}>
                  {(execution) => (
                    <tr
                      class="hover:bg-gray-800/50 transition-colors cursor-pointer"
                      onClick={() => handleSelectExecution(execution.id)}
                    >
                      <td class="px-4 py-3">
                        <span
                          class={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                            execution.status,
                          )}`}
                        >
                          <span class={execution.status === 'running' ? 'animate-pulse' : ''}>
                            {getStatusConfig(execution.status).icon}
                          </span>
                          <span>{getStatusConfig(execution.status).label}</span>
                        </span>
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex flex-col gap-1">
                          <Show when={execution.project_id}>
                            <span class="text-sm text-gray-300">
                              {getProjectName(execution.project_id)}
                            </span>
                          </Show>
                          <Show when={execution.agent_id}>
                            <span class="text-xs text-gray-500">
                              {getAgentName(execution.agent_id)}
                            </span>
                          </Show>
                          <Show when={!execution.project_id && !execution.agent_id}>
                            <span class="text-sm text-gray-500">-</span>
                          </Show>
                        </div>
                      </td>
                      <td class="px-4 py-3">
                        <p class="text-sm text-gray-300 max-w-xs truncate">
                          {execution.current_step || '-'}
                        </p>
                        <Show when={execution.error}>
                          <p class="text-xs text-red-400 mt-1 truncate max-w-xs">
                            {execution.error}
                          </p>
                        </Show>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-400">
                        {execution.started_at
                          ? formatRelativeTime(execution.started_at)
                          : formatRelativeTime(execution.created_at)}
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-400">
                        {formatDuration(execution.started_at, execution.completed_at)}
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-400">
                        {execution.tokens_used?.toLocaleString() || '-'}
                      </td>
                      <td class="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectExecution(execution.id);
                          }}
                          class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        {/* Pagination */}
        <Show when={totalPages() > 1}>
          <div class="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage() === 1}
              class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>

            <For
              each={Array.from({ length: Math.min(5, totalPages()) }, (_, i) => {
                const page = currentPage();
                const total = totalPages();
                if (total <= 5) return i + 1;
                if (page <= 3) return i + 1;
                if (page >= total - 2) return total - 4 + i;
                return page - 2 + i;
              })}
            >
              {(page) => (
                <button
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  class={`px-3 py-2 rounded-lg border transition-colors ${
                    currentPage() === page
                      ? 'bg-purple-600 border-purple-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
                >
                  {page}
                </button>
              )}
            </For>

            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages(), p + 1))}
              disabled={currentPage() === totalPages()}
              class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </Show>
      </Show>
    </div>
  );
};

export default Executions;
